// Cycle de vie d'une US — appels runtime (CCT-74).
// Le serveur scope tout à l'email du token. Les statuts VALIDATED/REFUSED ne sont PAS
// posables par le joueur : c'est l'inspection du moteur qui tranche (garde côté serveur).
import { apiFetch } from './client';

export type UsStatus =
  | 'BACKLOG' | 'ASSIGNED' | 'IN_PROGRESS' | 'BLOCKED'
  | 'TO_REVIEW' | 'REVIEWED' | 'VALIDATED' | 'REFUSED' | 'READY';

export interface UserStory {
  id: number;
  element_id: number;
  title: string | null;
  status: UsStatus;
  settable?: UsStatus[];
  controller: string | null;
  owner_character_id: number | null;
  abandoned?: boolean;
  abandon_effect?: 'unlock_next' | 'abandon_mission';
  /** L'auteur autorise la divulgation de la solution ET une template Solution existe (CCT-100).
   *  L'app n'affiche « Voir la solution » que si vrai ET la tâche terminée (validée/abandonnée). */
  has_solution?: boolean;
}

export interface ChatTurn {
  at?: string | null;
  role: 'character' | 'student';
  text: string;
  /** Les DEUX seules réponses possibles ; vide sur un tour du joueur. */
  replies: { id: string; label: string }[];
}

export interface UsChat {
  chat_id: number;
  element_id: string;
  /**
   * The thread's TONE, decided by the server — CCT-347.
   *
   * ⚠️ NOT A CLOSED UNION. It read `'blocked' | 'refused' | 'validated'`, and `handover` (CCT-345)
   * shipped without it: the type said one thing, the server another, and only the screen's
   * fallback kept the list readable. The app maps the tones it knows and falls back for the rest.
   */
  reason: string;
  closed: boolean;
  turns: ChatTurn[];
  character_name: string | null;
  avatarkey: string | null;
  /** "Analysis in progress" — set by the server while a hand-over awaits the engine, absent
   *  once the character has answered. `messageKey` is a `srv.*` key the app can translate. */
  pending?: { messageKey: string; message: string; messageType?: string } | null;
}

/** Résumé d'un fil du MOTEUR pour le centre de messages (CCT-97). */
export interface UsChatSummary {
  chatId: number;
  characterId: number | null;
  missionId: number;
  elementId: string;
  /**
   * The thread's TONE, decided by the server — CCT-347.
   *
   * ⚠️ NOT A CLOSED UNION. It read `'blocked' | 'refused' | 'validated'`, and `handover` (CCT-345)
   * shipped without it: the type said one thing, the server another, and only the screen's
   * fallback kept the list readable. The app maps the tones it knows and falls back for the rest.
   */
  reason: string;
  closed: boolean;
  updatedAt: string | null;
  characterName: string | null;
  avatarkey: string | null;
  missionTitle: string | null;
  lastMessage: string;
  turnCount: number;
}

const A = (token: string | null) => ({ token, auth: true as const });

/** Un message de la CHRONOLOGIE d'un personnage (tous fils fusionnés). */
export interface CharacterMessage {
  role: 'character' | 'student';
  text: string;
  at: string;
  missionId: number | null;
  missionTitle: string | null;
  elementId: string;
  /**
   * The thread's TONE, decided by the server — CCT-347.
   *
   * ⚠️ NOT A CLOSED UNION. It read `'blocked' | 'refused' | 'validated'`, and `handover` (CCT-345)
   * shipped without it: the type said one thing, the server another, and only the screen's
   * fallback kept the list readable. The app maps the tones it knows and falls back for the rest.
   */
  reason: string;
  chatId: number;
  replies: { id: string; label: string }[];
  closed: boolean;
}

export interface CharacterTimeline {
  characterId: number;
  characterName: string | null;
  avatarkey: string | null;
  missions: { missionId: number; title: string }[];
  messages: CharacterMessage[];
}

/** Tous les fils du moteur de l'apprenant (toutes missions confondues). */
/** Unread updates per area — CCT-348. The badges on Chat and Board. */
export interface AreaUpdates {
  chat: number;
  board: number;
}

/**
 * Counts what changed SINCE the player last looked at each area.
 *
 * ⚠️ THE MARKER IS THE CLIENT'S, AND IT IS SENT. Counting server-side against a marker the app
 * holds is one small call; downloading both lists to count them on screen would be two large ones
 * on every wake-up — and `/me/notifications` alone once kept the container awake 53 % of the time
 * (CCT-233). No marker (first run) means nothing unread, never everything.
 */
export const getUpdates = (token: string | null, chatSince: string | null, boardSince: string | null) => {
  const q = new URLSearchParams();
  if (chatSince) q.set('chatSince', chatSince);
  if (boardSince) q.set('boardSince', boardSince);
  // ⚠️ `background`: A BADGE COUNT IS NOT A GESTURE THE PLAYER MADE. This call fires on mount and
  // on every return of window focus; served as a foreground request it pinned "Syncing…" over an
  // idle conversation, for a counter nobody asked to refresh. Same rule as `/me/notifications`
  // (CCT-177): what the player did not ask for does not speak in their name.
  return apiFetch<AreaUpdates>(`/me/user-stories/updates?${q.toString()}`,
                               { ...A(token), background: true });
};

export const listUsChats = (token: string | null) =>
  apiFetch<{ chats: UsChatSummary[] }>('/me/user-stories/chats', A(token));

/** Chronologie complète des échanges avec UN personnage (option : filtrer sur une mission). */
export const getCharacterTimeline = (token: string | null, characterId: number, missionId?: number | null) =>
  apiFetch<CharacterTimeline>(
    `/me/user-stories/chats/character/${characterId}${missionId != null ? `?mission_id=${missionId}` : ''}`,
    A(token));

/** Une US du kanban : porte sa mission (le tableau de bord agrège toutes les missions). */
export interface BoardUserStory extends UserStory {
  mission_id: number;
  /** Le joueur a renoncé : « Abandonnée », pas « Refusée » — ce n'est pas la revue qui a
   *  tranché (CCT-100). Le statut moteur reste REFUSED, définitif. */
  abandoned?: boolean;
  /** Ce que coûte un abandon, tel que l'auteur l'a choisi : la tâche seule, ou la mission. */
  abandon_effect?: 'unlock_next' | 'abandon_mission';
  mission_title: string | null;
  /**
   * The identifier the player READS — `DSM-100-1`, CCT-350.
   *
   * ⚠️ THE SAME ONE THE CHARACTER SAYS. The engine writes "la user story $US.id" in its messages;
   * a screen that named the task its own way would offer a filter using words nobody else uses.
   * `null` when the task no longer has a place in the storyline (its key cannot be computed).
   */
  us_key?: string | null;
  status_changed_at: string | null;
  blocked_at: string | null;
  owner_name: string | null;
  owner_avatarkey: string | null;
}

/** Toutes les US de l'apprenant, toutes missions — kanban du tableau de bord (CCT-98). */
export const listAllUserStories = (token: string | null) =>
  apiFetch<{ userStories: BoardUserStory[]; settable: UsStatus[]; statuses: UsStatus[] }>(
    '/me/user-stories', A(token));

/** Une entrée du JOURNAL d'une US : changement de statut ou commentaire (CCT-99). */
export interface UsActivity {
  activity_id: number;
  kind: 'status' | 'comment';
  from_status: UsStatus | null;
  to_status: UsStatus | null;
  body: string | null;
  /** `owner` = le personnage propriétaire (changement de statut après revue). */
  author: 'character' | 'controller' | 'player' | 'owner' | null;
  character_id: number | null;
  created_at: string;
  character_name: string | null;
  avatarkey: string | null;
}

export const listUsActivity = (token: string | null, missionId: number, elementId: string | number) =>
  apiFetch<{ activity: UsActivity[] }>(
    `/me/user-stories/${missionId}/${elementId}/activity`, A(token));

export interface MissionCharacter {
  characterid: number;
  name: string;
  role: string | null;
  avatarkey: string | null;
  /** CCT-167 : le personnage incarné par l'apprenant — jamais proposé en mention (on ne se
   *  mentionne pas soi-même, comme il ne figure pas dans sa propre équipe, cf. CCT-155). */
  is_player?: boolean;
}

/** Personnages de la mission — autocomplete `@` des commentaires (CCT-99). */
export const listMissionCharacters = (token: string | null, missionId: number) =>
  apiFetch<{ characters: MissionCharacter[] }>(`/me/user-stories/${missionId}/characters`, A(token));

/** Commentaire du joueur ; chaque personnage invoqué par `@` répond en son nom. */
export const addUsComment = (token: string | null, missionId: number,
                             elementId: string | number, body: string) =>
  apiFetch<{ activity: UsActivity[] }>(
    `/me/user-stories/${missionId}/${elementId}/comments`,
    { ...A(token), method: 'POST', body: { body } });

/** Template SOLUTION servie en lecture seule (CCT-100) — le serveur seul décide si elle
 *  est accessible : autorisée par l'auteur ET tâche validée ou abandonnée. */
export interface SolutionTemplate {
  templateId: number;
  name: string | null;
  language: string | null;
  entrypoint: string | null;
  files: { path: string; content: string; mode: string }[];
  readOnly: boolean;
  runnable: boolean;
}

export const getUsSolution = (token: string | null, missionId: number, elementId: string | number) =>
  apiFetch<SolutionTemplate>(`/projects/solution/${missionId}/${elementId}`, A(token));

/** Abandon d'une tâche : REFUSED définitif. L'effet sur la mission est celui choisi par l'auteur. */
export const abandonUs = (token: string | null, missionId: number, elementId: string | number) =>
  apiFetch<{ abandoned: boolean; abandon_effect: string }>(
    `/me/user-stories/${missionId}/${elementId}/abandon`, { ...A(token), method: 'POST' });

/** Faits servis par le serveur quand AUCUNE US n'est rattachée — il est le seul à les voir. */
export interface UsDiagnostic {
  /** Nombre d'US ÉCRITES par l'auteur pour cette mission. */
  authored?: number;
  /** État de l'instance du moteur : `active`, `stopped`, ou absent si jamais créée. */
  engine?: string | null;
  /** Licence apprenant active ; `null` quand la base ne permet pas de le dire. */
  license?: boolean | null;
  /** Public mission: no licence is required there (see `_license_gate` server side). */
  public?: boolean | null;
}

export const listUserStories = (token: string | null, missionId: number) =>
  apiFetch<{ userStories: UserStory[]; settable: UsStatus[]; diagnostic?: UsDiagnostic }>(
    `/me/user-stories/${missionId}`, A(token));

export const setUsStatus = (token: string | null, missionId: number, elementId: string | number, status: UsStatus) =>
  apiFetch<UserStory>(`/me/user-stories/${missionId}/${elementId}/status`, {
    ...A(token), method: 'PATCH', body: { status },
  });

export const getUsChat = (token: string | null, missionId: number, elementId: string | number) =>
  apiFetch<UsChat>(`/me/user-stories/${missionId}/${elementId}/chat`, A(token));

/** `contextElementId` : US désignée depuis le volet, qui sert de context à la question. */
export const replyUsChat = (token: string | null, missionId: number, elementId: string | number,
                            choice: string, contextElementId?: string | number | null) =>
  apiFetch<UsChat>(`/me/user-stories/${missionId}/${elementId}/chat/reply`, {
    ...A(token), method: 'POST',
    body: contextElementId != null ? { choice, contextElementId: String(contextElementId) } : { choice },
  });
